import {
  Box,
  Button,
  Checkbox,
  Header,
  SegmentedControl,
  Select,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@cloudscape-design/components';
import { LotStatus } from '@parallel-fluidics/shared/constants';
import { groupLotsByItem } from '@parallel-fluidics/shared/models/lots';
import PPartDisplay from 'components/Projects/pPartDisplay';
import LotKindBadge from 'features/lots/components/LotKindBadge';
import LotStatusBadge from 'features/lots/components/LotStatusBadge';
import useLotsByItem from 'features/lots/hooks/useLotsByItem';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { kindOptions } from './lotFilterOptions';

/**
 * The By-item overview: one row per project+part with per-status bag and
 * remaining-part counts. A row expands into its bags (same fetched data,
 * no extra reads); every count links to the Bags view with the item +
 * status filter pre-applied. Statuses are the columns, so there is no
 * status filter here — only the Include Empty toggle for the one status
 * hidden by default.
 */
export default function ByItemTab({
  projectDocId = null,
  kind = 'all',
  includeEmpty = false,
  onFiltersChange,
  onClearFilters,
  projectFilterOptions,
  selectedProjectOption,
  projectsLoading,
}) {
  const navigate = useNavigate();
  const [expandedItems, setExpandedItems] = useState([]);
  const { data: lots, isLoading, error } = useLotsByItem({ projectDocId, kind, includeEmpty });

  // The query is ordered newest-first, so rows land in order of each
  // item's newest bag and expanded bags are already sorted. Rows render
  // as-is and their raw lots are the child rows — an item row is the one
  // with a `lots` array.
  const rows = useMemo(() => groupLotsByItem(lots || []), [lots]);

  const columnDefinitions = useMemo(() => {
    const statusColumns = [
      LotStatus.AWAITING_INSPECTION,
      LotStatus.SEALED,
      LotStatus.OPEN,
      LotStatus.QUARANTINED,
      ...(includeEmpty ? [LotStatus.EMPTY] : []),
    ];
    return [
      {
        id: 'item',
        header: 'Item',
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => (item.lots ? (
          <PPartDisplay
            fieldName="fullId"
            projectDocId={item.projectDocId}
            pPartDocId={item.pPartDocId}
            fallbackMessage={item.key}
          />
        ) : (
          <SpaceBetween direction="horizontal" size="xs" alignItems="center">
            <Button variant="inline-link" onClick={() => navigate(`/lots/${item.id}`)}>
              {item.code}
            </Button>
            <LotKindBadge kind={item.kind} />
            <LotStatusBadge status={item.status} />
          </SpaceBetween>
        )),
      },
      ...statusColumns.map((status) => ({
        id: status,
        header: status,
        // eslint-disable-next-line react/no-unstable-nested-components
        cell: (item) => {
          if (!item.lots) {
          // A bag shows its count under its own status column.
            return item.status === status
              ? `${item.remainingQty} / ${item.initialQty}` : '—';
          }
          const bucket = item.byStatus[status];
          if (!bucket) return '—';
          // Carry the active kind so the destination rows match the count.
          const kindParam = kind !== 'all' ? `&kind=${encodeURIComponent(kind)}` : '';
          return (
            <Button
              variant="inline-link"
              onClick={() => navigate(`/lots?projectDocId=${item.projectDocId}`
              + `&pPartDocId=${item.pPartDocId}&status=${encodeURIComponent(status)}${kindParam}`)}
            >
              {`${bucket.bags} bag${bucket.bags === 1 ? '' : 's'} · ${bucket.pcs} pcs`}
            </Button>
          );
        },
      })),
      {
        id: 'remaining',
        header: 'Usable parts',
        cell: (item) => (item.lots ? item.usableTotal : '—'),
      },
    ];
  }, [includeEmpty, kind, navigate]);

  return (
    <Table
      variant="full-page"
      header={(
        <Header
          variant="h2"
          counter={`(${rows.length})`}
          description="Bag counts and remaining parts per item (usable = not quarantined or awaiting inspection) — click a count for those bags, expand a row for its bags in place."
        >
          Lots by item
        </Header>
      )}
      filter={(
        <SpaceBetween direction="horizontal" size="s" alignItems="center">
          <div style={{ width: 280 }}>
            <Select
              selectedOption={selectedProjectOption}
              options={projectFilterOptions}
              statusType={projectsLoading ? 'loading' : 'finished'}
              filteringType="auto"
              expandToViewport
              onChange={({ detail }) => onFiltersChange({
                projectDocId: detail.selectedOption.value,
                pPartDocId: null,
              })}
            />
          </div>
          <SegmentedControl
            selectedId={kind}
            options={kindOptions}
            onChange={({ detail }) => onFiltersChange({ kind: detail.selectedId })}
          />
          <Checkbox
            checked={includeEmpty}
            onChange={({ detail }) => onFiltersChange({
              includeEmpty: detail.checked ? '1' : null,
            })}
          >
            Include Empty
          </Checkbox>
          {(projectDocId || kind !== 'all' || includeEmpty) && (
            <Button variant="inline-link" onClick={onClearFilters}>
              Clear filters
            </Button>
          )}
        </SpaceBetween>
      )}
      loading={isLoading}
      loadingText="Loading lots"
      items={rows}
      // Item rows track by their project/part key, bag rows by lot doc ID.
      trackBy={(item) => item.key || item.id}
      columnDefinitions={columnDefinitions}
      resizableColumns
      wrapLines
      expandableRows={{
        getItemChildren: (item) => item.lots || [],
        isItemExpandable: (item) => Boolean(item.lots),
        expandedItems,
        onExpandableItemToggle: ({ detail }) => setExpandedItems(
          (prev) => (detail.expanded
            ? [...prev, detail.item]
            : prev.filter((item) => item.key !== detail.item.key)),
        ),
      }}
      // A failed query must not read as an empty result. A refetch
      // failure with cached rows still renders the (stale) rows — React
      // Query retries in the background, so no banner for that.
      empty={error ? (
        <StatusIndicator type="error">
          {`Could not load lots: ${error.message}`}
        </StatusIndicator>
      ) : (
        <Box textAlign="center" color="text-status-inactive">
          No lots match these filters
        </Box>
      )}
    />
  );
}

ByItemTab.propTypes = {
  projectDocId: PropTypes.string,
  kind: PropTypes.string,
  includeEmpty: PropTypes.bool,
  onFiltersChange: PropTypes.func.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  projectFilterOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  })).isRequired,
  selectedProjectOption: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
  }).isRequired,
  projectsLoading: PropTypes.bool.isRequired,
};
